import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import moment from 'moment';
import Link from '~/components/Link/Link';
import LoadingSpinner from '~/components/LoadingSpinner/LoadingSpinner';
import StyledComponent from '~/components/Styled/Styled';
import CardHeader from '~/components/CardHeader/CardHeader';
import BaseballBases from '~/components/BaseballBases/BaseballBases';
import FootballTeamPossession from '~/components/FootballTeamPossession/FootballTeamPossession';
import matchupOperations from '~/state/matchup/operations';
import userLocation from '~/util/userLocation';
import { initStore } from '~/state/store';
import classnames from 'classnames/bind';
import { ResponsivePNG } from 'sports-react';

import css from '~/components/Matchup/Matchup.scss';
import withState from '~/hocs/withState';
import getOr from '~/util/getOr';

const cssContext = classnames.bind(css);
const isHomeFirst = league => league.sport_name === 'soccer';

const renderTeam = (
  redZone,
  sportName,
  team,
  boxScore,
  standings,
  eventStatus,
  alignment,
  ranking,
  isFirstTeam = false
) => {
  const shortRecord = get(standings, `${alignment}.short_record`, '');
  const formattedRank = get(standings, `${alignment}.formatted_rank`, '');
  const teamLogo = get(team, 'logos.small', '');
  const teamScore = get(boxScore, `score.${alignment}.score`, '-');
  const teamAbbreviation = get(team, 'abbreviation', '');
  const teamInPossession = get(boxScore, 'team_in_possession.abbreviation', '');
  const teamRowClasses = cssContext(css.teamRow, {
    separator: isFirstTeam,
  });
  const recordAndRank = [shortRecord, formattedRank].filter(obj => obj).join(', ');
  const altText = `${get(team, 'medium_name', '')} logo`;
  const awayTimeouts = get(boxScore, 'away_timeouts_left', 0);
  const homeTimeouts = get(boxScore, 'home_timeouts_left', 0);
  const segment = get(boxScore, 'progress.segment_string', '');
  const shootoutGoals = get(boxScore, `score.${alignment}.shootout`, 0);
  const isTeamDataEmpty = !!(!team || (typeof team === 'object' && Object.keys(team).length === 0));

  if (isTeamDataEmpty) {
    return (
      <div className={teamRowClasses}>
        <div className={css.teamName}>TBD</div>
      </div>
    );
  }

  const Timeouts = () => {
    if (alignment === 'home') {
      return [...Array(homeTimeouts).keys()].map(_ => <span className={css.timeout}>&nbsp;</span>);
    }

    if (alignment === 'away') {
      return [...Array(awayTimeouts).keys()].map(_ => <span className={css.timeout}>&nbsp;</span>);
    }

    return null;
  };

  const FootballPossessionIndicator = () => {
    if (
      sportName === 'football' &&
      eventStatus === 'in_progress' &&
      teamAbbreviation === teamInPossession
    ) {
      return <FootballTeamPossession fillColor={redZone ? '#ff0f00' : '#0078fd'} />;
    }

    return null;
  };

  return (
    <Link route={team.api_uri}>
      <a className={css.teamAnchor} aria-label={`${team.medium_name} team page`}>
        <div className={teamRowClasses}>
          <div className={css.logoWrapper}>
            {teamLogo ? (
              <ResponsivePNG className={css.logo} src={teamLogo} alt={altText} />
            ) : (
              <div className={css.logoDefault} />
            )}
          </div>
          <div className={css.teamDetail}>
            {ranking && <div className={css.teamName}>{`(${ranking})`}&nbsp;</div>}
            <div className={css.teamName}>{team.medium_name}</div>
            <div className={css.teamRecord}>{recordAndRank}</div>
          </div>
          {(eventStatus === 'in_progress' || eventStatus === 'final') && (
            <div className={css.teamScoreContainer}>
              <div className={css.teamScore}>
                <FootballPossessionIndicator />
                <span className={css.teamScoreNumber}>{teamScore}</span>
              </div>
              {eventStatus === 'final' && sportName === 'soccer' && segment === 'PK' ? (
                <div className={css.teamScore}>({shootoutGoals})</div>
              ) : null}
            </div>
          )}
        </div>
        <div className={css.timeouts}>
          {eventStatus === 'in_progress' &&
          (sportName === 'basketball' || sportName === 'football') ? (
            <Timeouts />
          ) : null}
        </div>
      </a>
    </Link>
  );
};

const AggregateResult = ({ event }) => {
  const aggregateResult = get(event, 'aggregate_result', '');

  if (aggregateResult) {
    return (
      <div className={css.seriesTracker}>
        <div className={css.round}>{aggregateResult.round_description}</div>
        <div className={css.description}>{aggregateResult.description}</div>
      </div>
    );
  }

  return null;
};

const BallPosition = ({ event }) => {
  const down = get(event, 'box_score.down', '');
  const formattedDistance = get(event, 'box_score.formatted_distance', '');
  const ballOn = get(event, 'box_score.ball_on', '');

  if (
    event.sport_name === 'football' &&
    event.event_status === 'in_progress' &&
    down &&
    formattedDistance &&
    ballOn
  ) {
    return (
      <div className={css.lastPlay}>
        <h3>Ball Position</h3>
        <div className={css.lastPlayContent}>
          {moment.localeData().ordinal(down)} and {formattedDistance} on {ballOn}
        </div>
      </div>
    );
  }

  return null;
};

const LastPlay = ({ event }) => {
  const boxScore = getOr(event, 'box_score', {});
  const eventStatus = getOr(event, 'event_status', '');
  const lastPlay =
    getOr(event, 'box_score.last_play.description', '') ||
    getOr(event, 'box_score.last_play.details', '');

  if (boxScore && lastPlay && eventStatus === 'in_progress') {
    return (
      <div className={css.lastPlay}>
        <h3>Last Play</h3>
        <div className={css.lastPlayContent}>{lastPlay}</div>
      </div>
    );
  }

  return null;
};

class Matchup extends React.Component {
  constructor() {
    super();

    this.state = {
      countryCode: 'us',
      loading: false,
    };

    this.updateMatchupData = this.updateMatchupData.bind(this);
    this.listenForViewChange = this.listenForViewChange.bind(this);
  }

  componentDidMount() {
    userLocation.promise.then(location => {
      this.setCountryCode(location.countryCode);
    });

    if (
      this.props.event.event_status === 'pre_game' ||
      this.props.event.event_status === 'in_progress' ||
      this.props.event.event_status === 'delayed'
    ) {
      window.addEventListener('visibilitychange', this.listenForViewChange);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('visibilitychange', this.listenForViewChange);
  }

  setCountryCode(countryCode = 'us') {
    this.setState({ countryCode: countryCode.toLowerCase() });
  }

  listenForViewChange() {
    if (document.visibilityState === 'visible') {
      this.updateMatchupData();
      this.setState({
        loading: true,
      });
    }
  }

  async updateMatchupData() {
    const { dispatch, event } = this.props;
    /* prettier-ignore */
    const {
      league: { slug },
      id,
    } = event;

    await dispatch(matchupOperations.fetchMatchup(slug, id));
    this.setState({
      loading: false,
    });
  }

  tvListing() {
    /* prettier-ignore */
    const {
      event: { tv_listings_by_country_code },
    } = this.props;
    const defaultTvListing = get(tv_listings_by_country_code, 'us[0].long_name', null);

    return get(
      tv_listings_by_country_code,
      `${this.state.countryCode}[0].long_name`,
      defaultTvListing
    );
  }

  clockLabel() {
    /* prettier-ignore */
    const {
      event: { event_status, box_score, game_date },
    } = this.props;
    const gameDate = moment(game_date).format('MMM D YYYY, h:mm A');

    // eslint-disable-next-line camelcase
    if (event_status !== 'pre_game' && box_score && box_score.progress) {
      switch (box_score.progress.status) {
        case 'pre_game':
          return gameDate;
        case 'postponed':
          return 'PPD';
        case 'half_over':
          return 'Halftime';
        case 'in_progress':
        case 'final':
        default:
          return get(box_score, 'progress.clock_label', gameDate);
      }
    }

    return gameDate;
  }

  renderTeams() {
    /* prettier-ignore */
    const { home_team, away_team, box_score, standings, league, event_status, sport_name } =
      this.props.event;
    const homeFirst = isHomeFirst(league);
    const homeRanking = get(this.props.event, 'top_25_rankings.home', null);
    const awayRanking = get(this.props.event, 'top_25_rankings.away', null);
    const redZone = get(this.props.event, 'red_zone', false);

    if (homeFirst) {
      return (
        <div>
          {renderTeam(
            redZone,
            sport_name,
            home_team,
            box_score,
            standings,
            event_status,
            'home',
            homeRanking,
            true
          )}
          {renderTeam(
            redZone,
            sport_name,
            away_team,
            box_score,
            standings,
            event_status,
            'away',
            awayRanking
          )}
        </div>
      );
    }

    return (
      <div>
        {renderTeam(
          redZone,
          sport_name,
          away_team,
          box_score,
          standings,
          event_status,
          'away',
          awayRanking,
          true
        )}
        {renderTeam(
          redZone,
          sport_name,
          home_team,
          box_score,
          standings,
          event_status,
          'home',
          homeRanking
        )}
      </div>
    );
  }

  render() {
    const { event } = this.props;

    const displayBaseballInningStatus =
      event.sport_name === 'baseball' &&
      event.event_status === 'in_progress' &&
      (event.box_score.progress.clock === 'Top' || event.box_score.progress.clock === 'Bot');

    return (
      <div className={css.matchup}>
        <CardHeader>
          <div className={css.matchupInfo}>
            <span className={css.clockLabel}>
              <div className={css.clockLabelAndFrame}>{this.clockLabel()}</div>
              {displayBaseballInningStatus ? (
                <span className={css.MLBMatchupInfo}>
                  <div>
                    B:{event.box_score.balls} S:{event.box_score.strikes} O:{event.box_score.outs}
                  </div>
                  <BaseballBases
                    firstBase={event.box_score.first_base}
                    secondBase={event.box_score.second_base}
                    thirdBase={event.box_score.third_base}
                  />
                </span>
              ) : null}
              {this.state.loading ? (
                <div className={css.loader}>
                  <LoadingSpinner />
                </div>
              ) : null}
            </span>
          </div>
          {Object.keys(event.tv_listings_by_country_code).length > 0 ? (
            <div className={css.tvListing}>
              <span className={css.tvListingIcon} />
              <span className={css.tvListingStation}>{this.tvListing()}</span>
            </div>
          ) : null}
        </CardHeader>
        {event.playoff && <div className={css.seriesTracker}>{event.playoff.description}</div>}

        <AggregateResult event={event} />

        {this.renderTeams()}

        <BallPosition event={event} />
        <LastPlay event={event} />
      </div>
    );
  }
}

const eventPropTypes = {
  event: PropTypes.shape({
    sport_name: PropTypes.string,
    box_score: PropTypes.object,
    league: PropTypes.object.isRequired,
    standings: PropTypes.object.isRequired,
    away_team: PropTypes.object,
    home_team: PropTypes.object,
    tv_listings_by_country_code: PropTypes.object.isRequired,
    game_date: PropTypes.string.isRequired,
    event_status: PropTypes.string.isRequired,
    top_25_rankings: PropTypes.object,
    playoff: PropTypes.shape({
      description: PropTypes.string,
    }),
    aggregate_result: PropTypes.shape({
      description: PropTypes.string,
      short_description: PropTypes.string,
      round_description: PropTypes.string,
    }),
  }).isRequired,
};

AggregateResult.displayName = 'AggregateResult';
AggregateResult.propTypes = {
  ...eventPropTypes,
};

LastPlay.displayName = 'LastPlay';
LastPlay.propTypes = {
  ...eventPropTypes,
};

BallPosition.displayName = 'BallPosition';
BallPosition.propTypes = {
  ...eventPropTypes,
};

Matchup.displayName = 'Matchup';
Matchup.propTypes = {
  ...eventPropTypes,
};

export default withState({ createStore: initStore })(StyledComponent(Matchup, [css]));
